/**
 * RawResponseDialog
 *
 * A simple developer tool: shows the exact sampled API response, pretty-printed,
 * in a scrollable modal. Lets the user verify what the connector actually
 * received before mapping/syncing - handy when a field looks off or a shape
 * was detected unexpectedly. Includes copy-to-clipboard and download.
 */

import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Typography,
  Stack, Button, Tooltip, useTheme, alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import CheckIcon from '@mui/icons-material/Check';

export default function RawResponseDialog({ open, onClose, data, truncated, title }) {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);

  const pretty = useMemo(() => {
    if (data == null) return '';
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }, [data]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pretty);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const download = () => {
    const blob = new Blob([pretty], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'response.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { height: '80vh' } }}>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title || 'Raw response'}
          </Typography>
          {truncated && (
            <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
              (truncated for size)
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Tooltip title={copied ? 'Copied' : 'Copy JSON'}>
            <IconButton size="small" onClick={copy}>
              {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Download JSON">
            <IconButton size="small" onClick={download}><DownloadIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {data == null ? (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">No sample response available.</Typography>
          </Box>
        ) : (
          <Box
            component="pre"
            sx={{
              m: 0, p: 2, flex: 1, overflow: 'auto',
              fontFamily: 'monospace', fontSize: 12.5, lineHeight: 1.5,
              whiteSpace: 'pre', tabSize: 2,
              bgcolor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.35 : 0.03),
              color: theme.palette.text.primary,
            }}
          >
            {pretty}
          </Box>
        )}
      </DialogContent>
      <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </Box>
    </Dialog>
  );
}
